import { Button, EmptyState, SearchInput, SegmentedControl, StatusPill } from "@flexfreight/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { Composer, type ComposeStart } from "./Composer";
import { errorText, MAILBOXES_KEY, when, type Draft, type Mailboxes, type Opened, type Summary } from "./emailApi";

type Folder = "inbox" | "sentitems" | "drafts";
const reSubject = (prefix: "Re" | "Fw", s: string) => (new RegExp(`^${prefix}:`, "i").test(s) ? s : `${prefix}: ${s}`);

/**
 * Email > Inbox (A.24): FlexFreight shared mailboxes read live from Microsoft 365 (only the ones this role may use),
 * with unread counts, threads, search, and drafts. Sending needs the owner-approved switch and a confirmation.
 */
export function InboxPage() {
  const me = useConsoleMe();
  const [params, setParams] = useSearchParams();
  const boxes = useQuery({ queryKey: MAILBOXES_KEY, queryFn: () => api<Mailboxes & { domains?: string[] }>("/v1/console/email/mailboxes") });
  const addresses = (boxes.data?.mailboxes ?? []).map((b) => b.address);
  const asked = params.get("mailbox");
  const mailbox = asked && addresses.includes(asked) ? asked : (addresses[0] ?? "");
  const folder = (params.get("folder") as Folder | null) ?? "inbox";
  const q = params.get("q") ?? "";
  const open = params.get("m");
  const [search, setSearch] = useState(q);
  const [compose, setCompose] = useState<{ start?: ComposeStart; draft?: Draft } | null>(null);
  const set = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "") n.delete(k);
          else n.set(k, v);
        }
        return n;
      },
      { replace: true },
    );
  useEffect(() => {
    const t = setTimeout(() => (search.trim() !== q ? set({ q: search.trim(), m: null }) : undefined), 300);
    return () => clearTimeout(t);
  }, [search]); // only the typed text drives the debounce
  const connected = !!boxes.data?.connected && mailbox !== "";
  const canSend = !!boxes.data?.canSend;
  const list = useQuery({
    queryKey: ["console", "mail", mailbox, folder, q],
    queryFn: () => api<{ messages: Summary[] }>(`/v1/console/email/messages?${new URLSearchParams({ mailbox, folder, q })}`),
    enabled: connected && folder !== "drafts",
  });
  const drafts = useQuery({
    queryKey: ["console", "drafts", mailbox],
    queryFn: () => api<{ drafts: Draft[] }>(`/v1/console/email/drafts?${new URLSearchParams({ mailbox })}`),
    enabled: connected && canSend,
  });
  const msg = useQuery({
    queryKey: ["console", "mail", mailbox, "msg", open],
    queryFn: () => api<Opened>(`/v1/console/email/messages/${encodeURIComponent(open!)}?${new URLSearchParams({ mailbox })}`),
    enabled: !!open && connected && folder !== "drafts",
  });

  if (boxes.data && (!boxes.data.connected || addresses.length === 0)) {
    return (
      <section className="ffa-page">
        <header className="ffa-head">
          <div>
            <h1 className="ffa-page__title">Inbox</h1>
            <p className="ffa-head__sub">FlexFreight shared mailboxes from Microsoft 365.</p>
          </div>
        </header>
        {boxes.data.connected ? (
          <EmptyState title="No mailboxes for your role" body="An admin can open a FlexFreight mailbox to your role in Admin Center > Integrations > Microsoft 365 mail." />
        ) : (
          <EmptyState
            title="Integration required: Microsoft 365"
            body={`${boxes.data.status === "Awaiting Microsoft 365 credentials" ? "Awaiting Microsoft 365 credentials. " : ""}Connect FlexFreight's Microsoft 365 app registration and list the FlexFreight mailboxes to read here. Only @flexfreight.net mailboxes can be connected; Empire mail never appears here.`}
            actions={me.capabilities.includes("integrations:manage") ? <Link className="ffx-btn ffx-btn--primary" to="/admin/integrations">Set up Microsoft 365</Link> : undefined}
          />
        )}
      </section>
    );
  }
  const sending = boxes.data?.sending;
  const draftRows = drafts.data?.drafts ?? [];
  const rows = list.data?.messages ?? [];
  const startReply = (kind: "reply" | "forward") => {
    const m = msg.data;
    if (!m) return;
    setCompose({ start: { mailbox, kind, sourceMessageId: m.id, to: kind === "reply" && m.from ? [m.from] : [], subject: reSubject(kind === "reply" ? "Re" : "Fw", m.subject) } });
  };
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Inbox</h1>
          <p className="ffa-head__sub">Shared FlexFreight mailboxes, read live from Microsoft 365. Drafts are saved here; nothing is emailed unless sending is approved and you confirm.</p>
        </div>
        <div className="ffa-head__actions">
          {sending ? <StatusPill tone={sending.on ? "success" : "warning"}>{sending.label}</StatusPill> : null}
          <Link className="ffx-btn" to="/inbox/templates">Templates</Link>
          {canSend ? <Button variant="primary" onClick={() => setCompose({ start: { mailbox, kind: "new" } })}>New message</Button> : null}
        </div>
      </header>
      <nav className="ffa-mailboxes" aria-label="Mailboxes">
        {(boxes.data?.mailboxes ?? []).map((b) => (
          <button key={b.address} type="button" className={`ffa-mailboxes__item${b.address === mailbox ? " is-on" : ""}`} aria-pressed={b.address === mailbox} onClick={() => set({ mailbox: b.address, m: null })}>
            <span className="ffa-mailboxes__name">{b.address}</span>
            {b.unread ? <span className="ffa-mailboxes__count" aria-label={`${b.unread} unread`}>{b.unread}</span> : null}
          </button>
        ))}
      </nav>
      <div className="ffa-mailbar">
        <SegmentedControl<Folder>
          label="Folder"
          options={[{ value: "inbox", label: "Inbox" }, { value: "sentitems", label: "Sent" }, ...(canSend ? [{ value: "drafts" as const, label: `Drafts${draftRows.length ? ` (${draftRows.length})` : ""}` }] : [])]}
          value={folder}
          onChange={(v) => set({ folder: v === "inbox" ? null : v, m: null })}
        />
        {folder !== "drafts" ? <SearchInput label="Search this mailbox" value={search} onChange={setSearch} /> : null}
      </div>
      {list.isError ? (
        <div className="ffa-notice" role="alert">
          <span>{errorText(list.error)}</span>
          <Button size="sm" onClick={() => void list.refetch()}>Try again</Button>
        </div>
      ) : null}
      {folder === "drafts" ? (
        <ul className="ffa-mail__list" aria-label={`Drafts in ${mailbox}`}>
          {drafts.isPending ? (
            <li className="ffa-muted">Loading...</li>
          ) : draftRows.length === 0 ? (
            <li className="ffa-muted">No drafts. Drafts you save stay here until they're sent or discarded.</li>
          ) : (
            draftRows.map((d) => (
              <li key={d.id}>
                <button type="button" className="ffa-mail__item" onClick={() => setCompose({ draft: d })}>
                  <span className="ffa-mail__from">
                    <StatusPill tone={d.status === "failed" ? "danger" : "warning"}>{d.status === "failed" ? "Draft · not sent" : "Draft"}</StatusPill> {d.to.join(", ") || "No recipients yet"}
                  </span>
                  <time className="ffa-muted ffa-small" dateTime={d.updatedAt}>{when.format(new Date(d.updatedAt))}</time>
                  <span className="ffa-mail__subject">{d.subject || "(no subject)"}</span>
                  <span className="ffa-muted ffa-small ffa-mail__preview">{d.kind === "new" ? "New message" : d.kind === "reply" ? "Reply" : "Forward"}{d.lastError ? ` · ${d.lastError}` : ""}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : (
        <div className={open ? "ffa-mail ffa-mail--open" : "ffa-mail"}>
          <ul className="ffa-mail__list" aria-label={`Messages in ${mailbox}`} aria-busy={list.isFetching}>
            {list.isPending ? (
              <li className="ffa-muted">Loading...</li>
            ) : rows.length === 0 ? (
              <li className="ffa-muted">No messages.</li>
            ) : (
              rows.map((m) => (
                <li key={m.id}>
                  <button type="button" className={`ffa-mail__item${m.id === open ? " is-open" : ""}${m.isRead ? "" : " is-unread"}`} onClick={() => set({ m: m.id })} aria-current={m.id === open ? "true" : undefined}>
                    <span className="ffa-mail__from">{m.fromName ?? m.from ?? "Unknown sender"}</span>
                    <time className="ffa-muted ffa-small" dateTime={m.receivedAt}>{when.format(new Date(m.receivedAt))}</time>
                    <span className="ffa-mail__subject">{m.subject}</span>
                    <span className="ffa-muted ffa-small ffa-mail__preview">{m.preview}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {open ? (
            <article className="ffx-card ffa-card ffa-mail__read" aria-label="Conversation">
              <Button size="sm" variant="ghost" className="ffa-mail__back" onClick={() => set({ m: null })}>← Back to messages</Button>
              {msg.isPending ? (
                <p className="ffa-muted">Loading...</p>
              ) : msg.isError || !msg.data ? (
                <p role="alert">{errorText(msg.error)}</p>
              ) : (
                <>
                  <div className="ffa-card__headrow">
                    <h2 className="ffa-card__title">{msg.data.subject}</h2>
                    {canSend ? (
                      <div className="ffa-mail__acts">
                        <Button size="sm" onClick={() => startReply("reply")}>Reply</Button>
                        <Button size="sm" onClick={() => startReply("forward")}>Forward</Button>
                      </div>
                    ) : null}
                  </div>
                  <p className="ffa-small ffa-muted">{msg.data.thread.length} message{msg.data.thread.length === 1 ? "" : "s"} in this conversation</p>
                  <ol className="ffa-convo">
                    {msg.data.thread.map((t) => (
                      <li key={t.id} className={`ffa-convo__msg${t.id === msg.data.id ? " is-open" : ""}`} aria-current={t.id === msg.data.id ? "true" : undefined}>
                        <dl className="ffa-dl">
                          <dt>From</dt>
                          <dd>{t.fromName ? `${t.fromName} <${t.from}>` : t.from}</dd>
                          <dt>To</dt>
                          <dd>{t.to.join(", ") || "-"}</dd>
                          {t.cc.length ? (
                            <>
                              <dt>Cc</dt>
                              <dd>{t.cc.join(", ")}</dd>
                            </>
                          ) : null}
                          <dt>Received</dt>
                          <dd>{when.format(new Date(t.receivedAt))}</dd>
                        </dl>
                        <p className="ffa-msg__body">{t.body}</p>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </article>
          ) : null}
        </div>
      )}
      {compose && sending ? <Composer {...compose} mailboxes={addresses} sending={sending} domains={boxes.data?.domains ?? ["flexfreight.net"]} onClose={() => setCompose(null)} /> : null}
    </section>
  );
}
